/**
 * Format time in minutes to a human-readable string
 * @param minutes - Time in minutes
 * @returns Formatted time string (e.g., "30 min", "1 hr 15 min")
 */
export function formatTime(minutes: number | undefined | null): string {
  if (!minutes || minutes <= 0) {
    return '0 min';
  }

  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  if (hours === 0) {
    return `${mins} min`;
  } else if (mins === 0) {
    return hours === 1 ? '1 hr' : `${hours} hrs`;
  } else {
    return `${hours} hr ${mins} min`;
  }
}

/**
 * Capitalize first letter of a string
 */
export function capitalize(str: string | undefined | null): string {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Capitalize first letter of each word (for lowercase reference labels).
 */
export function capitalizeWords(str: string | undefined | null): string {
  if (!str) return '';
  // Replace underscores with spaces so legacy slug-style values
  // (e.g. "main_dish") display as "Main Dish" instead of "Main_Dish".
  return str.replace(/_/g, ' ').replace(/\b[a-z]/g, char => char.toUpperCase());
}

/**
 * Format a date string
 */
export function formatDate(dateStr: string | undefined | null): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Get default recipe image URL
 */
export function getDefaultRecipeImage(): string {
  return (
    import.meta.env.VITE_DEFAULT_RECIPE_IMAGE ||
    'https://images.unsplash.com/photo-1495521821757-a1efb6729352?w=400&h=300&fit=crop'
  );
}

/**
 * Convert a decimal fractional part to a Unicode fraction character.
 * Uses a tolerance of ±0.01 to handle floating-point imprecision.
 */
const FRACTION_MAP: [number, string][] = [
  [0.125, '⅛'],
  [0.25, '¼'],
  [1 / 3, '⅓'],
  [0.375, '⅜'],
  [0.5, '½'],
  [0.625, '⅝'],
  [2 / 3, '⅔'],
  [0.75, '¾'],
  [0.875, '⅞'],
];

export function formatFraction(value: number): string {
  if (value < 0) return `-${formatFraction(-value)}`;
  const whole = Math.floor(value);
  const frac = value - whole;

  if (frac < 0.01) return whole.toString();

  const match = FRACTION_MAP.find(([f]) => Math.abs(frac - f) < 0.01);
  if (!match) {
    return parseFloat(value.toFixed(2)).toString();
  }

  return whole > 0 ? `${whole}${match[1]}` : match[1];
}

/**
 * Format an ingredient amount: apply a multiplier and display as a Unicode fraction.
 */
export function formatAmount(amount: number | null, multiplier = 1): string {
  if (amount === null) return '';
  const adjusted = amount * multiplier;
  return formatFraction(parseFloat(adjusted.toFixed(4)));
}

/**
 * Pluralize a unit for display when amount > 1.
 * Data is stored in singular form; this is only used at render time.
 */
const SINGULAR_DISPLAY: Record<string, string> = {
  tbsp: 'tablespoon',
  tsp: 'teaspoon',
  oz: 'oz',
  lb: 'lb',
  g: 'g',
  kg: 'kg',
  ml: 'ml',
  l: 'l',
};

const PLURAL_UNITS: Record<string, string> = {
  cup: 'cups',
  slice: 'slices',
  clove: 'cloves',
  bunch: 'bunches',
  pinch: 'pinches',
  dash: 'dashes',
  lb: 'lbs',
  tbsp: 'tablespoons',
  tsp: 'teaspoons',
  oz: 'oz',
  g: 'g',
  kg: 'kg',
  ml: 'ml',
  l: 'l',
};

export function pluralizeUnit(
  unit: string | null | undefined,
  amount: number | null | undefined,
  label?: string,
): string {
  if (!unit) return '';
  const key = unit.toLowerCase();
  // An admin-customized reference label wins verbatim — the plural maps only
  // understand the immutable stored values.
  if (label && label !== key) return label;
  // Plural only above 1, so fractional amounts read "½ cup", not "0.5 cups"
  if (amount !== null && amount !== undefined && amount > 1) {
    return PLURAL_UNITS[key] || unit;
  }
  return SINGULAR_DISPLAY[key] || unit;
}

/**
 * Generate a random color for collections
 */
export function randomCollectionColor(): string {
  const colors = ['#C45C26', '#7C9A6E', '#D4A853', '#5B8DB8', '#9B6B9E', '#C97B63'];
  return colors[Math.floor(Math.random() * colors.length)];
}
